import {
	asApiError,
	AutoCompleteResultFilter,
	DayOfWeek,
	IDailyIntervals,
	IInterval,
	IOperationResultNoValue,
	IUser,
	IUserReference,
	MeetingConfigViewModel,
	ResourceAutoCompleteViewModelType,
} from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useDebounceCallback } from '../../../../../../hooks/useDebounceValue';
import { useErrorMessages, useUserSession } from '../../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../../models/Logging';
import { getDisplayName } from '../../../../../../models/NameUtils';
import { useSchedulerConfigUpdateMaxPerDayMutation } from '../../../../../../queries/Scheduler/useUpdateMeetingConfigMaxPerDayMutation';
import { useSchedulerConfigUpdateMaxPerMonthMutation } from '../../../../../../queries/Scheduler/useUpdateMeetingConfigMaxPerMonthMutation';
import {
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../../autocomplete/SimpleAutoCompleteSearchField';
import { DeprecatedSelect, ISelectOption } from '../../../../DeprecatedSelect';
import { XIcon } from '../../../../svgs/icons/XIcon';
import { TextInput } from '../../../../TextInput';
import { Toggle } from '../../../../Toggle';
import { AvailabilityTimeProperty, MeetingConfigAvailability } from '../../MeetingConfigAvailability';
import { styleSheet } from '../styles';

interface IProps {
	meeting: MeetingConfigViewModel;
}

const defaultIncrement = 30;

const yesNoOptions: ISelectOption<boolean>[] = [
	{
		dataContext: false,
		id: 'yesno-option-no',
		text: 'No',
	},
	{
		dataContext: true,
		id: 'yesno-option-yes',
		text: 'Yes',
	},
];

const hoursOrDaysOptions: ISelectOption<string>[] = [
	{
		dataContext: 'days',
		id: 'hoursOrDays-option-days',
		text: 'days',
	},
	{
		dataContext: 'hours',
		id: 'hoursOrDays-option-hours',
		text: 'hours',
	},
];

const timeInAdvanceOptions: ISelectOption<number>[] = [
	1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30,
].map(n => ({
	dataContext: n,
	id: `time-in-advance-option-${n}`,
	text: `${n}`,
}));

const defaultInterval = {
	endMinutes: 1020,
	startMinutes: 540,
};

const defaultAvailability: IDailyIntervals[] = [
	null,
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval] },
	{ intervals: [defaultInterval, defaultInterval] },
	{ intervals: [defaultInterval] },

	null,
];

const lookForHoursOrDaysRegex = new RegExp(/([0-9]|[1-9][0-9])\./);

export const SchedulingLimits: React.FC<IProps> = observer(({ meeting }) => {
	const logger = useEventLogging();
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	// Create a filter to exclude the current user from the search results
	const userFilter = useMemo(() => {
		const currentUserId = userSession?.user?.id;

		// Function that checks if a user is not the current user
		const filterFn: AutoCompleteResultFilter<IUser> = (user, _query) => {
			if (!currentUserId) return true; // If no current user, include all results
			return user.id !== currentUserId; // Only include users that aren't the current user
		};

		return filterFn;
	}, [userSession?.user?.id]);

	const [meetingAvailability, setMeetingAvailability] = useState(meeting?.availability ?? defaultAvailability);
	const [allowSameDayMeetings, setAllowSameDayMeetings] = useState(
		meeting?.allowSameDay ? yesNoOptions[1] : yesNoOptions[0]
	);

	const matchIfHaveDays = meeting?.atLeastInterval.match(lookForHoursOrDaysRegex);

	const [hoursOrDays, setHoursOrDays] = useState<ISelectOption<string>>(
		hoursOrDaysOptions.find(i => {
			if (matchIfHaveDays) {
				return i.dataContext === 'days';
			}
			return i.dataContext === 'hours';
		}) ?? hoursOrDaysOptions[0]
	);

	const [meetingAtLeastInterval, setMeetingAtLeastInterval] = useState<ISelectOption<number>>(
		timeInAdvanceOptions.find(i => {
			if (matchIfHaveDays) {
				return i.dataContext === Number(matchIfHaveDays[0].replace('.', ''));
			}

			return i.dataContext === parseInt(meeting?.atLeastInterval.split(':')[0], 10);
		}) ?? timeInAdvanceOptions[0]
	);

	const [maxPerDay, setMaxPerDay] = useState(meeting?.maxPerDay || 0);
	const [maxPerMonth, setMaxPerMonth] = useState(meeting?.maxPerMonth || 0);
	const [additionalHosts, setAdditionalHosts] = useState<IUserReference[]>(meeting?.additionalHosts || []);

	useEffect(() => {
		meeting.setAsEditing()?.catch((err: IOperationResultNoValue) => {
			logger.logApiError('MeetingConfigSetAsEditing-Error', err);
			// do not need to show user that this failed...
		});
		meeting.getAutomationChoices();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onAllowSameDayMeetingsChange = (option: ISelectOption<boolean>) => {
		setAllowSameDayMeetings(option);
		meeting.allowSameDay = option.dataContext;
	};

	const onChangeHoursOrDays = (option: ISelectOption<string>) => {
		setHoursOrDays(option);
		setMeetingAtLeastInterval(timeInAdvanceOptions[0]);
		const hours = option.dataContext === 'hours' ? 1 : 0;
		const days = option.dataContext === 'days' ? 1 : 0;
		meeting.setAtLeastInterval(hours, days);
	};

	const onChangeMeetingAtLeastInterval = (option: ISelectOption<number>) => {
		setMeetingAtLeastInterval(option);
		const hours = hoursOrDays.dataContext === 'hours' ? option.dataContext : 0;
		const days = hoursOrDays.dataContext === 'days' ? option.dataContext : 0;
		meeting.setAtLeastInterval(hours, days);
	};

	const maxPerDayMutation = useSchedulerConfigUpdateMaxPerDayMutation({
		onSuccess: data => meeting.setConfig(data),
		onError: () => {
			setMaxPerDay(meeting.maxPerDay || 0);
			toast.error('Failed to update maximum meetings per day');
		},
		scope: { id: `meeting-config-maxPerDay-${meeting?.id}` },
	});

	const maxPerMonthMutation = useSchedulerConfigUpdateMaxPerMonthMutation({
		onSuccess: data => meeting.setConfig(data),
		onError: () => {
			setMaxPerMonth(meeting.maxPerMonth || 0);
			toast.error('Failed to update maximum meetings per month');
		},
		scope: { id: `meeting-config-maxPerMonth-${meeting?.id}` },
	});

	const debouncedSaveMaxPerDay = useDebounceCallback((num: number) => {
		maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: num, forUserId: meeting.forUser?.id });
	}, 500);

	const debouncedSaveMaxPerMonth = useDebounceCallback((num: number) => {
		maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: num, forUserId: meeting.forUser?.id });
	}, 500);

	const onMaxPerDayChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === '') {
			setMaxPerDay(undefined);
			debouncedSaveMaxPerDay(0);
			return;
		}
		const num = parseInt(e.target.value, 10);
		if (isNaN(num)) {
			setMaxPerDay(undefined);
			debouncedSaveMaxPerDay(0);
			return;
		}
		setMaxPerDay(num);
		debouncedSaveMaxPerDay(num);
	};

	const onMaxPerDayToggled = () => {
		debouncedSaveMaxPerDay.cancel();
		if (maxPerDay > 0) {
			setMaxPerDay(0);
			maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: 0, forUserId: meeting.forUser?.id });
			return;
		}
		setMaxPerDay(3);
		maxPerDayMutation.mutate({ configId: meeting.id, maxPerDay: 3, forUserId: meeting.forUser?.id });
	};

	const onMaxPerMonthChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === '') {
			setMaxPerMonth(undefined);
			debouncedSaveMaxPerMonth(0);
			return;
		}
		const num = parseInt(e.target.value, 10);
		if (isNaN(num)) {
			setMaxPerMonth(undefined);
			debouncedSaveMaxPerMonth(0);
			return;
		}
		setMaxPerMonth(num);
		debouncedSaveMaxPerMonth(num);
	};

	const onMaxPerMonthToggled = () => {
		debouncedSaveMaxPerMonth.cancel();
		if (maxPerMonth > 0) {
			setMaxPerMonth(0);
			maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: 0, forUserId: meeting.forUser?.id });
			return;
		}
		setMaxPerMonth(30);
		maxPerMonthMutation.mutate({ configId: meeting.id, maxPerMonth: 30, forUserId: meeting.forUser?.id });
	};

	const onTimeChange = (day: DayOfWeek, block: number, property: AvailabilityTimeProperty, value: number) => {
		const updated: any = { ...meetingAvailability[day] };
		if (updated?.intervals?.length) {
			updated.intervals[block][property] = value;

			// ensure that endMinutes are never earlier than startminutes
			if (property === 'startMinutes' && updated.intervals[block].endMinutes <= updated.intervals[block].startMinutes) {
				updated.intervals[block].endMinutes = updated.intervals[block].startMinutes + defaultIncrement;
			}

			meeting
				.setAvailability(day, updated)
				?.then(() => {
					setMeetingAvailability(meeting.availability);
				})
				?.catch((err: IOperationResultNoValue) => {
					logger.logApiError('ScheduleMeetingAvailabilityTimeChange-Error', err);

					errorMessages.pushApiError(err);
				});
			return;
		}

		toast.error('Error getting availabilities.');
		logger.logApiError(
			'MeetingConfigModal-onTimeChange-Error',
			asApiError(new Error('updated.intervals does not exist'))
		);
	};

	const addDay = (day: DayOfWeek) => {
		const update = { intervals: [defaultInterval] };
		updateAvailability(day, update);
	};

	const addSubblock = (day: DayOfWeek) => {
		const intervals = meetingAvailability[day].intervals;

		const lastInterval = intervals[intervals?.length - 1];
		const newInterval: IInterval = {
			endMinutes: lastInterval?.endMinutes + defaultIncrement * 2,

			startMinutes: lastInterval?.endMinutes + defaultIncrement,
		};
		const update = {
			intervals: [...intervals, newInterval],
		};
		updateAvailability(day, update);
	};

	const removeSubblock = (day: DayOfWeek) => {
		const newIntervals = meetingAvailability[day].intervals;

		newIntervals.pop();
		const update = {
			intervals: newIntervals,
		};
		updateAvailability(day, update);
	};

	const updateAvailability = (day: DayOfWeek, update: IDailyIntervals) => {
		meeting
			.setAvailability(day, update)
			?.then(() => {
				setMeetingAvailability(
					meetingAvailability.map((a, i) => {
						if (i === day) {
							a = update;
						}

						return a;
					})
				);
			})
			?.catch((err: IOperationResultNoValue) => {
				logger.logApiError('ScheduleMeetingAvailabilityAddDay-Error', err);

				errorMessages.pushApiError(err);
			});
	};

	const toggleAvailableDay = (day: number) => {
		const updated = meetingAvailability.map((a, idx) => {
			return meetingAvailability[day]
				? idx === day
					? null
					: a
				: idx === day
					? {
							intervals: [
								{
									endMinutes: 1020,
									startMinutes: 540,
								},
							],
						}
					: a;
		});

		meeting

			.setAvailability(day, updated[day])
			?.then(() => {
				setMeetingAvailability(updated);
			})
			?.catch((err: IOperationResultNoValue) => {
				logger.logApiError('ScheduleMeetingAvailability-Error', err);

				errorMessages.pushApiError(err);
			});
	};

	const onUserSelected = (e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<IUser>) => {
		const user = e.selection;
		if (user && !additionalHosts.some(host => host.id === user.id) && additionalHosts.length < 3) {
			const userReference: IUserReference = {
				id: user.id,
				firstName: user.firstName,
				lastName: user.lastName,
				email: user.email,
			};

			const newHosts = [...additionalHosts, userReference];
			setAdditionalHosts(newHosts);
			meeting.setAdditionalHosts(newHosts);
			if (e.target) {
				e.target.clearInput();
			}
		}
	};

	const removeAdditionalHost = (userId: string) => {
		const newHosts = additionalHosts.filter(host => host.id !== userId);
		setAdditionalHosts(newHosts);
		meeting.setAdditionalHosts(newHosts);
	};

	const isHoursInAdvance = hoursOrDays.dataContext === 'hours';

	return (
		<div className={css(styleSheet.wrapper)}>
			<div className={css(styleSheet.col)}>
				<h3 className={css(styleSheet.sectionTitle)}>Scheduling Limits</h3>
				<p className={css(styleSheet.label)}>Scheduling Limits</p>
				<div className={css(styleSheet.subLabel)}>
					<b>Allow invitees to schedule immediately...</b>
				</div>
				<DeprecatedSelect
					styles={[styleSheet.smallInput]}
					options={yesNoOptions}
					onOptionClick={onAllowSameDayMeetingsChange}
					selectedOption={allowSameDayMeetings}
				/>
				{!allowSameDayMeetings.dataContext && (
					<div className={css(styleSheet.numberInputContainer)}>
						<span className={css(styleSheet.text)}>Invitees must schedule meetings&nbsp;&nbsp;</span>
						<DeprecatedSelect
							styles={[styleSheet.smallInput, styleSheet.numberInput, styleSheet.timeInAdvanceSelect]}
							options={[...timeInAdvanceOptions].slice(0, isHoursInAdvance ? 23 : 30)}
							onOptionClick={onChangeMeetingAtLeastInterval}
							selectedOption={meetingAtLeastInterval}
						/>
						&nbsp;
						<DeprecatedSelect
							styles={[styleSheet.hoursOrDaysSelect]}
							options={hoursOrDaysOptions}
							onOptionClick={onChangeHoursOrDays}
							selectedOption={hoursOrDays}
						/>
						&nbsp;
						<span className={css(styleSheet.text)}>in advance</span>
					</div>
				)}
				<div className={css(styleSheet.spacer)} />
				<p className={css(styleSheet.label)}>Include others on this meeting</p>
				<div className={css(styleSheet.subLabel)}>
					The calendar will only show times when all participants are available
				</div>
				<div className={css(styleSheet.additionalHostsContainer)}>
					<SimpleAutoCompleteSearchField
						placeholder='Search employees...'
						type={ResourceAutoCompleteViewModelType.User}
						// @ts-ignore - The type definition might be slightly off
						onItemSelected={onUserSelected}
						resultsLimit={5}
						pageSize={5}
						className={css(styleSheet.additionalHostsInput)}
						disabled={additionalHosts.length >= 3}
						autoCompleteFilter={userFilter}
					/>
					{additionalHosts.length > 0 && (
						<div className={css(styleSheet.additionalHostsList)}>
							{additionalHosts.map(host => (
								<div key={host.id} className={css(styleSheet.additionalHostItem)}>
									<div className={css(styleSheet.hostDetails)}>
										<span className={css(styleSheet.additionalHostName)}>{getDisplayName(host)}</span>
										<span className={css(styleSheet.additionalHostEmail)}>{host.email}</span>
									</div>
									<button
										className={css(styleSheet.removeHostButton)}
										onClick={() => removeAdditionalHost(host.id)}
										aria-label={`Remove ${getDisplayName(host)}`}
									>
										<XIcon height={12} width={12} />
									</button>
								</div>
							))}
						</div>
					)}
				</div>
				<div className={css(styleSheet.spacer)} />
				<p className={css(styleSheet.label)}>Max Meeting Limits</p>
				<div className={css(styleSheet.subLabel)}>
					<b>Setup the max amount of meetings of this type allowed</b>
				</div>
				<div className={css(styleSheet.numberInputContainer)}>
					<Toggle
						id='max-same-day-meetings-toggle'
						isOn={maxPerDay > 0}
						onToggleCheckChanged={onMaxPerDayToggled}
						uncheckedColor='#ccc'
						className={css(styleSheet.toggle)}
					/>
					<TextInput
						className={css(styleSheet.smallInput, styleSheet.numberInput)}
						inputId='max-same-day-meetings-input'
						min={1}
						max={10}
						onChange={onMaxPerDayChanged}
						step={1}
						type='number'
						value={maxPerDay}
					/>
					<span className={css(styleSheet.text, maxPerDay === 0 && styleSheet.textDisabled)}>
						Maximum meetings per <u>day</u>
					</span>
				</div>
				<div className={css(styleSheet.numberInputContainer)}>
					<Toggle
						id='max-same-month-meetings-toggle'
						isOn={maxPerMonth > 0}
						onToggleCheckChanged={onMaxPerMonthToggled}
						uncheckedColor='#ccc'
						className={css(styleSheet.toggle)}
					/>
					<TextInput
						className={css(styleSheet.smallInput, styleSheet.numberInput)}
						inputId='max-same-month-meetings-input'
						min={1}
						max={50}
						onChange={onMaxPerMonthChanged}
						step={1}
						type='number'
						value={maxPerMonth}
						disabled={maxPerMonth === 0}
					/>
					<span className={css(styleSheet.text, maxPerMonth === 0 && styleSheet.textDisabled)}>
						Maximum meetings per <u>month</u>
					</span>
				</div>
				<div className={css(styleSheet.spacer)} />
			</div>
			<div className={css(styleSheet.col)}>
				<div className={css(styleSheet.availabilitiesContainer)}>
					<MeetingConfigAvailability
						meetingAvailability={meetingAvailability}
						onAddBlock={addSubblock}
						onAddDay={addDay}
						onChangeTime={onTimeChange}
						onRemoveBlock={removeSubblock}
						onToggleAvailable={toggleAvailableDay}
						increment={defaultIncrement}
					/>
				</div>
			</div>
		</div>
	);
});
